<template>
  <div class="step-date-field">
    <v-text-field
      ref="field"
      :value="display"
      class="step-date-field_input"
      :class="{ 'step-date-field_input__open': !!step }"
      inputmode="numeric"
      autocomplete="off"
      spellcheck="false"
      hide-details
      flat
      solo
      :autofocus="autofocus"
      @focus="onFocus"
      @blur="onBlur"
      @click="onClick"
      @keydown="onKeyDown"
      @keyup="onKeyUp"
      @paste="onPaste"
      @input="onNativeInput"
    >
      <template v-if="todayButton" #append>
        <!-- mousedown.prevent: поле не теряет фокус, панель не схлопывается -->
        <button
          type="button"
          class="step-date-field_today"
          tabindex="-1"
          @mousedown.prevent
          @click.stop="setToday"
        >
          Сегодня
        </button>
      </template>
    </v-text-field>

    <v-expand-transition>
      <div
        v-if="step"
        class="step-date-field_picker"
        @mousedown.prevent
      >
        <div class="step-date-field_steps">
          <button
            v-for="item in STEPS"
            :key="item.key"
            type="button"
            class="step-date-field_step"
            :class="{
              'step-date-field_step__active': step === item.key,
              'step-date-field_step__filled': isPartFilled(item.key),
            }"
            tabindex="-1"
            @click="openStep(item.key)"
          >
            <span class="step-date-field_step-label">{{ item.label }}</span>
            <span class="step-date-field_step-value">{{ partText(item.key) || '—' }}</span>
          </button>
        </div>

        <div
          v-if="step === 'year'"
          ref="years"
          class="step-date-field_grid step-date-field_grid__years"
        >
          <button
            v-for="year in years"
            :key="year"
            type="button"
            class="step-date-field_cell"
            :class="{ 'step-date-field_cell__selected': year === selectedYear }"
            :data-year="year"
            tabindex="-1"
            @click="pick('year', String(year))"
          >
            {{ year }}
          </button>
        </div>

        <div
          v-else-if="step === 'month'"
          class="step-date-field_grid step-date-field_grid__months"
        >
          <button
            v-for="(name, index) in MONTHS"
            :key="name"
            type="button"
            class="step-date-field_cell"
            :class="{ 'step-date-field_cell__selected': index + 1 === selectedMonth }"
            tabindex="-1"
            @click="pick('month', pad2(index + 1))"
          >
            {{ name }}
          </button>
        </div>

        <div
          v-else
          class="step-date-field_grid step-date-field_grid__days"
        >
          <button
            v-for="day in daysInMonth"
            :key="day"
            type="button"
            class="step-date-field_cell"
            :class="{ 'step-date-field_cell__selected': day === selectedDay }"
            tabindex="-1"
            @click="pick('day', pad2(day))"
          >
            {{ day }}
          </button>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
  import moment from 'moment'

  // Позиции цифр в строке «ДД.ММ.ГГГГ»: слоты 0-1 день, 2-3 месяц, 4-7 год
  const DIGIT_POSITIONS = [0, 1, 3, 4, 6, 7, 8, 9]
  const SLOTS = 8
  const PARTS = {
    day: { slots: [0, 1], caret: 0 },
    month: { slots: [2, 3], caret: 3 },
    year: { slots: [4, 5, 6, 7], caret: 6 },
  }
  // Порядок пошагового выбора: сначала год, потом месяц, потом день
  const STEP_ORDER = ['year', 'month', 'day']
  const DATE_FORMAT = 'DD.MM.YYYY'

  /**
   * Поле даты «ДД.ММ.ГГГГ» с пошаговым выбором год → месяц → день.
   *
   * Маска своя, не v-mask: незаполненные части показываются подчёркиваниями
   * («__.__.1991»), и курсор можно ставить в любую часть. Ручной ввод не
   * блокируется, панель выбора следует за той частью даты, где стоит курсор.
   *
   * v-model - строка «ДД.ММ.ГГГГ», пока дата заполнена не целиком - пустая строка.
   */
  export default {
    name: 'StepDateField',
    props: {
      value: { type: String, default: '' },
      autofocus: { type: Boolean, default: false },
      // Кнопка «Сегодня» в конце поля: подставляет день и месяц, открывает выбор года
      todayButton: { type: Boolean, default: false },
      // Диапазон лет в панели выбора, от текущего года назад
      yearsBack: { type: Number, default: 100 },
    },
    data () {
      return {
        slots: Array(SLOTS).fill(''),
        // Открытая часть панели: 'year' | 'month' | 'day' | null (свёрнута)
        step: null,
        lastEmitted: null,
        STEPS: [
          { key: 'year', label: 'Год' },
          { key: 'month', label: 'Месяц' },
          { key: 'day', label: 'День' },
        ],
        MONTHS: [
          'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
          'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
        ],
      }
    },
    computed: {
      display () {
        const s = this.slots.map(d => d || '_')
        return `${s[0]}${s[1]}.${s[2]}${s[3]}.${s[4]}${s[5]}${s[6]}${s[7]}`
      },
      isComplete () {
        return this.slots.every(d => d !== '')
      },
      fullValue () {
        return this.isComplete ? this.display : ''
      },
      selectedDay () {
        return this.isPartFilled('day') ? Number(this.partText('day')) : null
      },
      selectedMonth () {
        return this.isPartFilled('month') ? Number(this.partText('month')) : null
      },
      selectedYear () {
        return this.isPartFilled('year') ? Number(this.partText('year')) : null
      },
      years () {
        const current = moment().year()
        const list = []
        for (let year = current; year >= current - this.yearsBack; year--) {
          list.push(year)
        }
        return list
      },
      // Пока месяц не выбран - 31, февраль без года - 29: лишние дни отсечёт проверка даты
      daysInMonth () {
        if (!this.selectedMonth || this.selectedMonth > 12) {
          return 31
        }
        const year = this.selectedYear || 2000
        return moment({ year, month: this.selectedMonth - 1 }).daysInMonth()
      },
    },
    watch: {
      value: {
        immediate: true,
        handler (val) {
          if (val === this.lastEmitted) {
            return
          }
          this.setFromString(val)
        },
      },
      fullValue (val) {
        this.lastEmitted = val
        this.$emit('input', val)
      },
    },
    methods: {
      pad2 (n) {
        return String(n).padStart(2, '0')
      },
      partText (part) {
        return PARTS[part].slots.map(i => this.slots[i]).join('')
      },
      isPartFilled (part) {
        return PARTS[part].slots.every(i => this.slots[i] !== '')
      },
      partAtCaret (pos) {
        if (pos <= 2) return 'day'
        if (pos <= 5) return 'month'
        return 'year'
      },
      firstEmptyStep () {
        return STEP_ORDER.find(part => !this.isPartFilled(part)) || null
      },
      input () {
        return this.$refs.field ? this.$refs.field.$refs.input : null
      },
      caret () {
        const input = this.input()
        return input ? input.selectionStart : 0
      },
      setCaret (pos) {
        this.$nextTick(() => {
          const input = this.input()
          if (!input) return
          // v-text-field держит своё lazyValue: после отменённого keydown DOM и
          // Vue расходятся, выравниваем оба перед установкой курсора
          if (input.value !== this.display) {
            input.value = this.display
          }
          this.$refs.field.lazyValue = this.display
          input.setSelectionRange(pos, pos)
        })
      },
      setFromString (val) {
        const digits = moment(val || '', DATE_FORMAT, true).isValid()
          ? val.replace(/\D/g, '')
          : ''
        this.slots = Array.from({ length: SLOTS }, (_, i) => digits[i] || '')
      },
      setSlot (index, digit) {
        this.$set(this.slots, index, digit)
      },
      focus () {
        const input = this.input()
        if (input) input.focus()
      },

      // --- панель выбора ---
      // Показать шаг, курсор не трогать (панель следует за курсором)
      showStep (step) {
        this.step = step
        if (step === 'year') {
          this.$nextTick(this.scrollYearIntoView)
        }
      },
      // Открыть шаг и поставить курсор в начало его части
      openStep (step) {
        this.showStep(step)
        this.setCaret(PARTS[step].caret)
      },
      syncStepToCaret () {
        this.showStep(this.partAtCaret(this.caret()))
      },
      scrollYearIntoView () {
        const grid = this.$refs.years
        if (!grid) return
        const year = this.selectedYear || moment().year() - 30
        const cell = grid.querySelector(`[data-year="${year}"]`)
        if (cell) {
          grid.scrollTop = cell.offsetTop - grid.clientHeight / 2 + cell.offsetHeight / 2
        }
      },
      setPart (part, text) {
        PARTS[part].slots.forEach((slotIndex, i) => this.setSlot(slotIndex, text[i]))
      },
      pick (part, text) {
        this.setPart(part, text)
        this.advance()
      },
      // Следующая незаполненная часть в порядке год → месяц → день, иначе свернуть
      advance () {
        const next = this.firstEmptyStep()
        if (next) {
          this.openStep(next)
        } else {
          this.step = null
          this.setCaret(this.display.length)
        }
      },
      // День и месяц сегодняшние, год выбирается: день рождения - это дата в прошлом
      setToday () {
        const today = moment()
        this.setPart('day', today.format('DD'))
        this.setPart('month', today.format('MM'))
        this.focus()
        this.openStep('year')
      },

      // --- поле ---
      isEmpty () {
        return this.slots.every(d => d === '')
      },
      onFocus () {
        if (this.isEmpty()) {
          this.openStep('year')
        } else {
          this.syncStepToCaret()
        }
      },
      // Пустое поле начинаем с года, в заполненном панель следует за кликом
      onClick () {
        if (this.isEmpty()) {
          this.openStep('year')
        } else {
          this.syncStepToCaret()
        }
      },
      onBlur () {
        this.step = null
      },
      onKeyDown (event) {
        if (event.key === 'Enter') {
          this.$emit('enter', event)
          return
        }
        if (event.metaKey || event.ctrlKey || event.altKey) {
          return
        }
        if (/^\d$/.test(event.key)) {
          event.preventDefault()
          this.typeDigit(event.key)
        } else if (event.key === 'Backspace') {
          event.preventDefault()
          this.eraseBefore()
        } else if (event.key === 'Delete') {
          event.preventDefault()
          this.eraseAt()
        }
      },
      // Стрелки, Home/End: курсор переставлен браузером - панель следует за ним
      onKeyUp (event) {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          this.syncStepToCaret()
        }
      },
      afterEdit (caretPos) {
        if (this.isComplete) {
          this.step = null
          this.setCaret(caretPos)
        } else {
          this.step = this.partAtCaret(caretPos)
          this.setCaret(caretPos)
          if (this.step === 'year') {
            this.$nextTick(this.scrollYearIntoView)
          }
        }
      },
      typeDigit (digit) {
        const pos = this.caret()
        const index = DIGIT_POSITIONS.findIndex(p => p >= pos)
        if (index === -1) return
        this.setSlot(index, digit)
        let next = DIGIT_POSITIONS[index] + 1
        if (next === 2 || next === 5) next++
        this.afterEdit(next)
      },
      eraseBefore () {
        const pos = this.caret()
        let index = -1
        DIGIT_POSITIONS.forEach((p, i) => { if (p < pos) index = i })
        if (index === -1) return
        this.setSlot(index, '')
        this.afterEdit(DIGIT_POSITIONS[index])
      },
      eraseAt () {
        const pos = this.caret()
        const index = DIGIT_POSITIONS.findIndex(p => p >= pos)
        if (index === -1) return
        this.setSlot(index, '')
        this.afterEdit(DIGIT_POSITIONS[index])
      },
      fillFromDigits (digits) {
        const list = digits.replace(/\D/g, '').slice(0, SLOTS)
        for (let i = 0; i < SLOTS; i++) {
          this.setSlot(i, list[i] || '')
        }
        const filled = list.length
        const caretPos = filled ? DIGIT_POSITIONS[filled - 1] + 1 : 0
        this.afterEdit(filled === SLOTS ? this.display.length : caretPos)
      },
      onPaste (event) {
        event.preventDefault()
        const text = (event.clipboardData || window.clipboardData).getData('text')
        this.fillFromDigits(text)
      },
      // Запасной путь для ввода мимо keydown (экранные клавиатуры):
      // берём цифры из того, что оказалось в поле, и раскладываем по слотам
      onNativeInput (raw) {
        if (raw === this.display) return
        this.fillFromDigits(raw || '')
      },
    },
  }
</script>

<style lang="less">
// Не scoped: поле живёт в диалогах, которые рендерятся вне корня родителя,
// а стилизуются внутренности Vuetify. Префикс step-date-field уникален
.step-date-field {
  // Поле - как телефон и имя в создании клиента (CreateClient .text-field)
  &_input {
    .v-input__slot {
      height: 80px;
      padding: 0 0 0 20px !important;
      background-color: @color-secondary !important;
      border: 2px solid transparent;
      border-radius: 15px;
      transition: border-color 0.15s ease-out;
      // «Сегодня» заливает правый край, скругление поля его обрезает
      overflow: hidden;
    }

    &.v-input--is-focused .v-input__slot {
      border-color: #ff9500;
    }

    .v-text-field__slot input {
      font-size: 25px;
      font-weight: 500;
      line-height: 1;
      letter-spacing: 0.04em;
      font-variant-numeric: tabular-nums;
      color: #111;
      max-height: none;
      padding: 0;
      caret-color: @color-primary;
    }

  }

  // Vuetify центрирует append-inner в solo-поле правилом с тремя классами
  // (.v-text-field.v-text-field--solo .v-input__append-inner); здесь четыре,
  // чтобы растянуть его на всю высоту поля и кнопку внутри - без процентных высот
  .step-date-field_input.v-text-field--solo .v-input__append-inner {
    display: flex;
    align-self: stretch;
    align-items: stretch;
    margin: 0;
    padding: 0;
  }

  // «Сегодня» - продолжение поля, как лупа в поиске шапки (emit-search-btn):
  // та же заливка, во всю высоту, слева тонкий разделитель
  &_today {
    position: relative;
    display: inline-flex;
    align-items: center;
    align-self: stretch;
    padding: 0 24px;
    background: transparent;
    font-size: 16px;
    font-weight: 500;
    color: @color-primary;
    white-space: nowrap;
    cursor: pointer;
    transition: background-color 0.15s ease-out;

    &:before {
      content: '';
      position: absolute;
      left: 0;
      top: 20%;
      bottom: 20%;
      width: 1px;
      background-color: #dbdfe6;
    }

    &:hover {
      background: #e3e4e7;
    }

    &:active {
      background: darken(#e3e4e7, 5%);
    }
  }

  // Панель выбора под полем
  &_picker {
    margin-top: 8px;
    padding: 12px;
    border: 1px solid #e3e6eb;
    border-radius: 15px;
    background: #fff;
  }

  // Шаги: год · месяц · день. Активный подчёркнут, заполненный показывает значение
  &_steps {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 12px;
  }

  &_step {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 8px 12px;
    border-radius: 8px;
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease-out;

    // Подчёркивание активного шага - отдельная полоска: рамка и тень
    // загибались вместе со скруглением кнопки
    &:after {
      content: '';
      position: absolute;
      left: 12px;
      right: 12px;
      bottom: 0;
      height: 2px;
      border-radius: 2px;
      background: transparent;
      transition: background-color 0.15s ease-out;
    }

    &:hover {
      background: #f7f8fa;
    }

    &-label {
      font-size: 12px;
      line-height: 1.2;
      color: #8a8f99;
    }

    &-value {
      font-size: 16px;
      font-weight: 500;
      line-height: 1.2;
      color: #b4b9c2;
      font-variant-numeric: tabular-nums;
    }

    &__filled &-value {
      color: #111;
    }

    &__active {
      &:after {
        background: @color-primary;
      }

      .step-date-field_step-label {
        color: @color-primary;
      }
    }
  }

  &_grid {
    display: grid;
    gap: 6px;

    &__years {
      // offsetTop ячеек считается от сетки - для прокрутки к нужному году
      position: relative;
      grid-template-columns: repeat(6, 1fr);
      max-height: 196px;
      overflow-y: auto;
      // Место под скролл-бар, чтобы ячейки не прыгали между шагами
      padding-right: 4px;
      scroll-behavior: smooth;

      @media (prefers-reduced-motion: reduce) {
        scroll-behavior: auto;
      }
    }

    &__months {
      grid-template-columns: repeat(4, 1fr);
    }

    &__days {
      grid-template-columns: repeat(7, 1fr);
    }
  }

  &_cell {
    height: 40px;
    border-radius: 8px;
    background: @color-secondary;
    font-size: 15px;
    font-weight: 500;
    color: #111;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    transition: background-color 0.15s ease-out, color 0.15s ease-out;

    &:hover {
      background: #e3e4e7;
    }

    &__selected,
    &__selected:hover {
      background: @color-primary;
      color: #fff;
    }
  }

  @media (max-width: 600px) {
    &_grid__years {
      grid-template-columns: repeat(4, 1fr);
    }

    &_cell {
      font-size: 14px;
    }
  }
}
</style>
